import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useMemo } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';

import { useUserDeleteDialog } from './UserDeleteDialog.hooks';
import type { UserDeleteDialogProps } from './UserDeleteDialog.types';

export const UserDeleteDialog = ({
  open,
  onClose,
  userId
}: UserDeleteDialogProps) => {
  const {
    data: user,
    deleteMode,
    deleteUser,
    isFetching,
    selectedOrganization,
    setDeleteMode,
    userOrganizations
  } = useUserDeleteDialog(userId);

  const handleDialogClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    deleteUser.mutate(user!, {
      onSuccess: () => {
        enqueueSnackbar(`User ${user?.name} Deleted`, {
          variant: 'success'
        });
        onClose('success');
      }
    });
  };

  const deleteAllLabel = useMemo(
    () => (
      <Box>
        <Box sx={{ mb: 1, mt: 1.5, fontWeight: 'bold' }}>
          Delete from all organizations
        </Box>
        <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
          {userOrganizations.map((org) => (
            <Chip key={org} label={org} />
          ))}
        </Stack>
      </Box>
    ),
    [userOrganizations]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteMode((event.target as HTMLInputElement).value as 'one');
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth>
      <DialogTitle>
        Delete User: {user?.name} (#{user?.id})
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {isFetching && <LoadingIndicator isLoading />}
        <Box sx={{ my: 2 }}>
          <Alert severity="warning">
            Are you sure you want to delete this user account? This action is
            irreversible and will permanently delete all data associated with
            this user account.
          </Alert>
        </Box>
        {userOrganizations.length > 1 && (
          <Box sx={{ my: 2 }}>
            <RadioGroup
              value={deleteMode}
              onChange={handleChange}
              name="radio-buttons-group"
            >
              <FormControlLabel
                value="one"
                control={<Radio />}
                disabled={selectedOrganization.id === 0}
                label={
                  <strong>
                    Delete from current organization (
                    {selectedOrganization.name}) only
                  </strong>
                }
              />
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                value="all"
                control={<Radio />}
                label={deleteAllLabel}
              />
            </RadioGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          type="submit"
          onClick={handleSubmit}
          disabled={isFetching}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDeleteDialog;
